import { API, ApiError } from './api';

interface ErrorDetail { name?: string; message: string; stack?: string }

/** Errors already sent, so one failure caught and rethrown along the way is reported once. */
const reported = new WeakSet<object>();
/** A loop of failures stops reporting after this many in one page load. */
const MAX_REPORTS = 20;
let sent = 0;

/** The error and everything it was caused by, such as the pdf.js error behind "Could not render PDF page 1." */
function describe(error: unknown): ErrorDetail[] {
  const chain: ErrorDetail[] = [];
  for (let current = error; current !== undefined && current !== null && chain.length < 5; current = current instanceof Error ? current.cause : undefined) {
    chain.push(current instanceof Error ? { name: current.name, message: current.message.slice(0, 1000), stack: current.stack?.slice(0, 2000) } : { message: String(current).slice(0, 500) });
  }
  return chain;
}

/** Someone leaving the page or replacing a request is not a failure. */
const cancelled = (error: unknown) => error instanceof DOMException && error.name === 'AbortError';

/**
 * Logs an error the person only saw as a general message, and sends it to the Worker's log so a failure on someone
 * else's computer can be read afterwards. A server's refusal (`ApiError`) explains itself and is skipped; failures to
 * reach the server are reported where the request is made. Details hold identifiers, never answers or file contents.
 * Reporting never fails the caller.
 */
export function reportClientError(where: string, error: unknown, details: Record<string, unknown> = {}): void {
  if (error instanceof ApiError || cancelled(error)) return;
  if (error && typeof error === 'object') { if (reported.has(error)) return; reported.add(error); }
  console.error(where, error, details);
  if (sent >= MAX_REPORTS || typeof fetch !== 'function') return;
  sent++;
  try {
    const report = { where, errors: describe(error), details, url: globalThis.location?.pathname, devicePixelRatio: globalThis.devicePixelRatio };
    void fetch(`${API}/client-errors`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(report), credentials: 'same-origin', keepalive: true }).catch(() => {});
  } catch { /* Reporting is best effort. */ }
}

/** `fetch`, reporting when the server can't be reached or fails (5xx). Refusals (4xx) are left to the caller. */
export async function reportingFetch(input: string, init?: RequestInit): Promise<Response> {
  const path = input.split('?')[0];
  let response: Response;
  try { response = await fetch(input, init); }
  catch (error) { reportClientError('fetch: unreachable', error, { path, method: init?.method ?? 'GET' }); throw error; }
  if (response.status >= 500) reportClientError('fetch: server error', new Error(`${response.status} from ${path}`), { path, method: init?.method ?? 'GET', status: response.status });
  return response;
}

/** Errors nothing caught, including a component that fails to render. */
export function reportUncaughtErrors(): void {
  addEventListener('error', event => reportClientError('uncaught', event.error ?? event.message, { source: event.filename, line: event.lineno, column: event.colno }));
  addEventListener('unhandledrejection', event => reportClientError('unhandled rejection', event.reason));
}
